<template>
  <!--
    ONE layer for the whole page. It renders nothing until a card is open, then becomes a fixed,
    click-through frame the size of the screen that the card (and, on touch, a shield or a dimmed
    backdrop) sit in — so "inside the frame" IS "inside the viewport", for the e2e geometry check.
  -->
  <div
    class="sv-cl"
    :class="open ? ['sv-cl--open', `sv-cl--${mode}`] : ''"
    data-test="sv-checklist-layer"
  >
    <template v-if="open">
      <!-- Touch popover: a transparent page-wide shield, so a tap anywhere else closes the card. -->
      <div v-if="mode === 'pop'" class="sv-cl__shield" data-test="sv-checklist-shield" @click="close"></div>

      <!-- Phone: the page dims behind the sheet, and a tap on the dim closes it. -->
      <div v-if="mode === 'sheet'" class="sv-cl__backdrop" data-test="sv-checklist-backdrop" @click="close"></div>

      <VisitChecklistCard
        :key="openAptNum"
        ref="card"
        class="sv-cl__card"
        :class="`sv-cl__card--${mode}`"
        :style="cardStyle"
        :appt="openAppt"
        :categories="view.categories"
        :checked="checkedMap"
        :variant="mode"
        :focus-category="focusKey"
        :accent="accent"
        :op-name="opName"
        :provider-name="providerName"
        :status-text="statusText"
        :bucket="bucket"
        :broken="broken"
        :pending="!alertPayload"
        :incomplete="incomplete"
        @mouseenter.native="keep"
        @mouseleave.native="onCardLeave"
        @toggle="onToggle"
        @open-tab="onOpenTab"
        @close="close"
      />
    </template>
  </div>
</template>

<script>
/**
 * The visit checklist's ONE layer (Phase 10e) — owned by ScheduleView.vue.
 *
 * The card used to live inside each appointment block, `pointer-events: none`, so it could be read
 * and never touched. A checklist you can tick has to be clickable, so it moved here: one card for the
 * whole page, opened by whichever block asks.
 *
 * Three modes, decided by `checklistMode()` from what the DEVICE can do (not its width alone):
 *   hover — a mouse. Resting on a block opens the card; the pointer can travel into it.
 *   pop   — touch at tablet width. The chip on a block opens an anchored popover.
 *   sheet — touch at phone width. The chip opens a bottom sheet.
 *
 * ⛔ THE CARD IS LIVE. This component holds only WHICH appointment is open (AptNum) and where it was
 * anchored. The appointment, its checklist and its ticks are looked up on every render, so a status
 * change or a tick from another screen shows in the open card, and an appointment that leaves the
 * day (a live refresh, a filter) closes it instead of leaving a stale copy on screen.
 *
 * ⛔ ESCAPE: a plain bubble-phase listener that does nothing — and does not preventDefault — when no
 * card is open. The Patient Panel handles Escape itself in the capture phase; this must never
 * swallow or double-handle a key that is not ours.
 *
 * ⛔ No prop may be named `patNum`, `siteID`, `loading` or `allSettings`: mixin.js defines computeds
 * with those names and a computed SILENTLY REPLACES a same-named prop.
 */
import VisitChecklistCard from './VisitChecklistCard.vue';
import { ALERT_CATEGORY, checkKey } from '@/utils/scheduleView/checklist';
import { hasAlertErrors } from '@/utils/scheduleView/alerts';
import {
  HOVER_CAPABLE_MEDIA_QUERY, CARD_GRACE_MS, checklistMode, hoverOpenDelay,
} from '@/utils/scheduleView/checklistLayer';
import { PHONE_MEDIA_QUERY, hoverCardPosition, HOVER_CARD_WIDTH, operatoryPalette } from '@/utils/scheduleView/layout';
import { resolveBucket, statusLabel, isBroken } from '@/utils/scheduleView/statusBuckets';

const EMPTY_VIEW = { categories: [], totals: { todo: 0, done: 0, fyi: 0 } };

export default {
  name: 'ChecklistLayer',

  components: { VisitChecklistCard },

  props: {
    /** The FILTERED day's appointments — an open card whose appointment leaves this list closes. */
    appointments: { type: Array, default: () => [] },
    /** The UNFILTERED day, for the chair's name and colour and the provider's name. */
    unfilteredDay: { type: Object, default: null },
    bucketMap: { type: Object, default: () => ({}) },
    /** ChecklistByAptNum from the page: { '<AptNum>': { alertKeys, categories, totals } }. */
    checklists: { type: Object, default: () => ({}) },
    /** The /alerts payload, or null before it arrives (the card must not claim "nothing open"). */
    alertPayload: { type: Object, default: null },
    /** The day on screen; a change closes the card. */
    date: { type: String, default: '' },
  },

  data() {
    return {
      /** 'hover' until detection says otherwise (also the answer where matchMedia does not exist). */
      mode: 'hover',
      isPhone: false,
      /** The open appointment's AptNum, or null. Deliberately NOT the appointment object. */
      openAptNum: null,
      /** The block's viewport rect when it was opened. */
      anchor: null,
      /** The category to highlight (an alert icon was hovered). */
      focusKey: null,
      /** The card's fixed position once measured; null while it is being measured (hidden). */
      placement: null,
      measuredHeight: 0,
    };
  },

  computed: {
    panelOpen() {
      const pp = this.$store && this.$store.state.patientPanel;
      return !!(pp && pp.isOpen);
    },

    storeChecks() {
      const sv = this.$store && this.$store.state.scheduleView;
      return (sv && sv.checks) || {};
    },

    /** The open appointment, looked up fresh from the day; null once it has left it. */
    openAppt() {
      if (this.openAptNum === null) return null;
      return this.appointments.find(a => a.AptNum === this.openAptNum) || null;
    },

    open() {
      return this.openAptNum !== null && !!this.openAppt;
    },

    view() {
      return (this.openAppt && this.checklists[this.openAppt.AptNum]) || EMPTY_VIEW;
    },

    /** itemId -> true for the open appointment's ticks. */
    checkedMap() {
      const out = {};
      if (this.openAptNum === null) return out;
      this.view.categories.forEach((c) => {
        c.items.forEach((it) => {
          if (this.storeChecks[checkKey(this.openAptNum, it.id)]) out[it.id] = true;
        });
      });
      return out;
    },

    incomplete() {
      return hasAlertErrors(this.alertPayload);
    },

    palette() {
      return operatoryPalette(this.unfilteredDay ? this.unfilteredDay.operatories : []);
    },

    operatory() {
      if (!this.openAppt || !this.unfilteredDay) return null;
      return (this.unfilteredDay.operatories || []).find(o => o.OperatoryNum === this.openAppt.Op) || null;
    },

    opName() {
      const op = this.operatory;
      return op ? (op.OpName || op.Abbrev || '') : '';
    },

    accent() {
      const c = this.openAppt ? this.palette[this.openAppt.Op] : null;
      return c ? c.accent : '#9ca4a2';
    },

    providerName() {
      if (!this.openAppt || !this.unfilteredDay) return '';
      const a = this.openAppt;
      // The hygienist for a hygiene visit, otherwise the dentist — appointmentProviderNum's rule,
      // kept inline so this file needs only the helpers it already imports.
      const provNum = (Number(a.IsHygiene) === 1 && Number(a.ProvHyg) > 0) ? Number(a.ProvHyg) : Number(a.ProvNum);
      const p = (this.unfilteredDay.providers || []).find(x => x && x.ProvNum === provNum);
      if (!p) return '';
      return `${p.FName || ''} ${p.LName || ''}`.trim() || p.Abbr || '';
    },

    bucket() {
      return this.openAppt ? resolveBucket(this.openAppt, this.bucketMap) : 'unconfirmed';
    },

    statusText() {
      return this.openAppt ? statusLabel(this.openAppt, this.bucket) : '';
    },

    broken() {
      return !!this.openAppt && isBroken(this.openAppt);
    },

    cardStyle() {
      if (this.mode === 'sheet') return { maxHeight: '88%' };
      // Hidden (but laid out, so it can be measured) until the first placement.
      return this.placement || {
        top: '0px',
        left: '0px',
        width: `${HOVER_CARD_WIDTH}px`,
        maxHeight: 'calc(100vh - 20px)',
        visibility: 'hidden',
      };
    },
  },

  watch: {
    openAptNum() {
      this.placement = null;
      this.measuredHeight = 0;
      this.$nextTick(this.place);
    },

    /** The appointment left the filtered day (live refresh, filter change): the card goes with it. */
    openAppt(appt) {
      if (this.openAptNum !== null && !appt) this.close();
    },

    /** The Patient Panel is the thing being looked at now. */
    panelOpen(isOpen) {
      if (isOpen) this.close();
    },

    date() {
      this.close();
    },
  },

  created() {
    // Not reactive: bookkeeping that must not re-render the layer.
    this.openTimer = null;
    this.hideTimer = null;
    this.pendingFocus = null;
    this.mediaWatches = [];
    this.detectMode();
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown);
    // Capture phase, on the window: scroll events do not bubble, and the page itself (not only the
    // grid) can scroll. Either one leaves a position:fixed card floating where its block USED to be.
    window.addEventListener('scroll', this.onScroll, true);
    window.addEventListener('resize', this.onResize);
  },

  updated() {
    // The card's height can change under it (a live refresh adds a line): re-place it, but only on a
    // real change — placing sets data, which re-renders, which would otherwise loop.
    const card = this.$refs.card;
    if (this.placement && card && card.$el && Math.abs(card.$el.offsetHeight - this.measuredHeight) > 1) {
      this.place();
    }
  },

  beforeDestroy() {
    this.clearTimers();
    window.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onResize);
    this.mediaWatches.forEach(({ mql, onChange }) => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    });
    this.mediaWatches = [];
  },

  methods: {
    // ── mode ─────────────────────────────────────────────────────────────────────────────────────
    /** Follow BOTH queries live: an iPad gaining a mouse, or a window crossing the phone width. */
    detectMode() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        this.$emit('mode', this.mode);
        return;
      }
      const hover = window.matchMedia(HOVER_CAPABLE_MEDIA_QUERY);
      const phone = window.matchMedia(PHONE_MEDIA_QUERY);
      const onChange = () => this.applyMode(!!hover.matches, !!phone.matches);
      [hover, phone].forEach((mql) => {
        // Safari < 14 — older operatory iPads — only has the deprecated addListener.
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else if (mql.addListener) mql.addListener(onChange);
        this.mediaWatches.push({ mql, onChange });
      });
      onChange();
    },

    applyMode(hoverCapable, isPhone) {
      const next = checklistMode({ hoverCapable, isPhone });
      // A swap between the grid and the phone timeline, or a different mode, leaves a card anchored
      // to a block that is no longer there.
      if (next !== this.mode || isPhone !== this.isPhone) this.close();
      this.isPhone = isPhone;
      this.mode = next;
      this.$emit('mode', next);
    },

    // ── hover (mouse) ────────────────────────────────────────────────────────────────────────────
    /**
     * The pointer entered a block (or keyboard focus reached it). The card opens after the pointer
     * RESTS — 300 ms, or 90 ms when another card is already showing — so sweeping across a column
     * does not strobe a card at every block. Focus opens at once: a keyboard user is not "passing by".
     */
    enter({ appt, el, via } = {}) {
      if (this.mode !== 'hover' || !appt || this.panelOpen) return;
      this.clearTimers();
      if (via === 'focus') {
        this.pendingFocus = null;
        this.openFor(appt.AptNum, el, null);
        return;
      }
      if (this.openAptNum === appt.AptNum) return;
      this.pendingFocus = null;
      this.openTimer = setTimeout(() => {
        this.openTimer = null;
        this.openFor(appt.AptNum, el, this.pendingFocus);
      }, hoverOpenDelay(this.openAptNum !== null));
    },

    /** The pointer left the block (or the card): close after a grace period, so it can cross the gap. */
    leave() {
      if (this.mode !== 'hover') return;
      this.clearTimers();
      this.hideTimer = setTimeout(() => {
        this.hideTimer = null;
        this.close();
      }, CARD_GRACE_MS);
    },

    /** The pointer reached the card: it stays. */
    keep() {
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer = null;
    },

    onCardLeave() {
      this.leave();
    },

    /** An alert icon was hovered: highlight (and scroll to) its category once the card is up. */
    focusAlert({ appt, key } = {}) {
      if (this.mode !== 'hover' || !appt) return;
      const cat = ALERT_CATEGORY[key] || null;
      this.pendingFocus = cat;
      if (this.openAptNum === appt.AptNum) this.focusKey = cat;
    },

    // ── touch (chip) ─────────────────────────────────────────────────────────────────────────────
    /** The chip was pressed: open this appointment's card, or close it if it is the one showing. */
    toggle({ appt, el } = {}) {
      if (this.mode === 'hover' || !appt || this.panelOpen) return;
      this.clearTimers();
      if (this.openAptNum === appt.AptNum) {
        this.close();
        return;
      }
      this.openFor(appt.AptNum, el, null);
    },

    // ── open / close ─────────────────────────────────────────────────────────────────────────────
    openFor(aptNum, el, focus) {
      const rect = el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
      // A hovered block that has since left the page measures zero wide: opening a card beside
      // nothing would float it in a corner. (A chip press always has its block; a sheet needs no
      // anchor at all.)
      if (this.mode === 'hover' && !(rect && rect.width > 0)) return;
      this.anchor = rect
        ? { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height }
        : { top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 };
      this.focusKey = focus || null;
      this.openAptNum = aptNum;
    },

    close() {
      this.clearTimers();
      this.openAptNum = null;
      this.anchor = null;
      this.focusKey = null;
      this.placement = null;
      this.pendingFocus = null;
    },

    clearTimers() {
      if (this.openTimer) clearTimeout(this.openTimer);
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.openTimer = null;
      this.hideTimer = null;
    },

    /**
     * Place the card beside its block using its MEASURED height — the old tooltip guessed, and a
     * guess is wrong exactly when it matters (a card with many categories near the bottom of the
     * screen). It is hidden until this runs, so it never flashes at 0,0.
     */
    place() {
      const card = this.$refs.card;
      if (!card || !card.$el || !this.anchor || this.mode === 'sheet') return;
      const height = card.$el.offsetHeight;
      this.measuredHeight = height;
      // The position's own cap wins: below or above a block the card gets only the space that is
      // there (hoverCardPosition → stackedCardPosition), and the window-high default would let it
      // spill back onto the block.
      this.placement = Object.assign(
        { maxHeight: 'calc(100vh - 20px)' },
        hoverCardPosition(this.anchor, { width: window.innerWidth, height: window.innerHeight }, height)
      );
    },

    // ── window listeners ─────────────────────────────────────────────────────────────────────────
    onKeydown(e) {
      // Nothing open ⇒ not ours: no close, and above all no preventDefault.
      if (this.openAptNum === null) return;
      if (e && e.key === 'Escape') this.close();
    },

    onScroll(e) {
      if (this.openAptNum === null) return;
      // The card's own body scrolls (it has a max-height); that must not close it.
      const card = this.$refs.card && this.$refs.card.$el;
      const target = e && e.target;
      if (card && target && target.nodeType && card.contains(target)) return;
      this.close();
    },

    onResize() {
      // A rotated iPad or a resized window leaves a fixed card where its block used to be.
      if (this.openAptNum !== null) this.close();
    },

    // ── what the card asks for ───────────────────────────────────────────────────────────────────
    async onToggle({ itemKey, checked } = {}) {
      const aptNum = this.openAptNum;
      if (aptNum === null || !itemKey) return;
      const result = await this.$store.dispatch('scheduleView/toggleCheck', { aptNum, itemKey, checked: !!checked });
      // The store is free of UI, so the failure is reported here (and the tick has been rolled back).
      if (result && result.ok === false && this.$toast && typeof this.$toast.error === 'function') {
        this.$toast.error(result.error || 'Could not save the checkmark.');
      }
    },

    onOpenTab(tab) {
      const appt = this.openAppt;
      this.close();
      if (appt) this.$emit('open-tab', { appt, tab });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

// Closed: an empty, unstyled element — no box, nothing to click.
.sv-cl--open {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  // Click-through, so the frame never blocks the page; the card, shield and backdrop opt back in.
  pointer-events: none;
  // Above the AI agent's floating button (agent-panel.css, 9999 !important) in EVERY mode. Found on
  // the real page (2026-10-12): at 60 the button painted over the corner of a tablet popover, where
  // a category's arrow sits, so a tap meant for the arrow opened the agent instead.
  // ⛔ That is above the Patient Panel (1035) too. The card is never drawn over the panel because
  // the two are never on screen together, not because of stacking: the layer closes when the panel
  // opens and neither a hover nor a chip opens it while the panel is up (`panelOpen`).
  // The shield, backdrop and cards below are ordered INSIDE this element's stacking context.
  z-index: 10000;
}

.sv-cl__shield {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 59;
  pointer-events: auto;
}

.sv-cl__backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  pointer-events: auto;
  background: rgba(21, 32, 26, 0.32);
}

.sv-cl__card {
  pointer-events: auto;
  // ⛔ A global link font otherwise wins over a control's own.
  font-family: inherit;
}

.sv-cl__card--hover,
.sv-cl__card--pop {
  position: fixed;
  z-index: 60;
  max-width: calc(100vw - 16px);
}

.sv-cl__card--sheet {
  position: fixed;
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10001;
  width: auto;
  // The home-indicator strip on a notched iPhone.
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
